import { useEffect } from "react";

import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { Controller, useForm } from "react-hook-form";
import StyledDialog from "../../../../../common/components/dialog/StyledDialog";
import confirmDialogStyleOptions from "../../../../../common/helpers/confirmDialogParams";
import { useGetCustomer } from "../../../../../common/hooks/useGetCustomer";
import { skuCatalog } from "../../../../../common/models/enums";
import { DEFAULT_DEVICE_PASSWORD } from "../../../../../common/variables/common";
import { errorNotification } from "../../../../../common/variables/notification";
import { useCreateSKU } from "../hooks/useCreateSKU";
import { useUpdateSKU } from "../hooks/useUpdateSKU";
import {
  ALLOWED_MODELS_BY_TYPE,
  ISKUForm,
  defaultSKUForm,
} from "../variables/skuData";
import SKUFormItem from "./SKUFormItem";

const TYPE_ORDER = ["CH4", "VOC", "MULT", "OPT", "THM"];

const sortSKUConfigurations = (
  configurations: ISKUForm["skuDataConfigurations"],
  subComponents: { name: string; type: string }[]
) => {
  return [...configurations].sort((a, b) => {
    const subA = subComponents.find(s => s.name === a.name);
    const subB = subComponents.find(s => s.name === b.name);
    const typeA = subA?.type ?? "";
    const typeB = subB?.type ?? "";

    // AGX nodes come first
    if (typeA === "AGX" && typeB !== "AGX") return -1;

    if (typeA !== "AGX" && typeB === "AGX") return 1;

    // Devices: sort by assigned node first
    const nodeA = a.defaultNodeName ?? "";
    const nodeB = b.defaultNodeName ?? "";

    if (nodeA !== nodeB)
      return nodeA.localeCompare(nodeB, undefined, { numeric: true });

    // Same node: sort by device type
    const orderA = TYPE_ORDER.indexOf(typeA);
    const orderB = TYPE_ORDER.indexOf(typeB);
    const typeOrderA = orderA === -1 ? Infinity : orderA;
    const typeOrderB = orderB === -1 ? Infinity : orderB;

    if (typeOrderA !== typeOrderB) return typeOrderA - typeOrderB;

    // Same node and type: sort by name
    return a.name.localeCompare(b.name, undefined, { numeric: true });
  });
};

interface CreateSKUDialogProps {
  open: boolean;
  handleClose: () => void;
  mode?: "create" | "duplicate" | "edit";
  id?: string;
  initialValues?: ISKUForm;
  existingConfigurationNames?: string[];
}

const CreateSKUDialog = ({
  open,
  handleClose,
  mode = "create",
  id,
  initialValues,
  existingConfigurationNames,
}: CreateSKUDialogProps): JSX.Element => {
  const confirm = useConfirm();
  const { createSKU, loading: creating } = useCreateSKU();
  const { updateSKU, loading: updating } = useUpdateSKU();
  const loading = mode === "edit" ? updating : creating;
  const { customer } = useGetCustomer();

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ISKUForm>({
    defaultValues: initialValues ?? defaultSKUForm,
    mode: "onBlur",
  });

  useEffect(() => {
    if (mode === "create") {
      setValue("password", customer?.devicePassword || DEFAULT_DEVICE_PASSWORD);
    }
  }, [customer?.devicePassword, mode]);

  const watchedSKUNumber = watch("skuNumber");
  const watchedSKUData = watch("skuDataConfigurations");

  // Propagate shared IP from first CC_MULT component to all others
  useEffect(() => {
    const catalogEntry = watchedSKUNumber ? skuCatalog[watchedSKUNumber] : null;

    if (!catalogEntry) return;

    const multIndices: number[] = [];

    watchedSKUData.forEach((cfg, i) => {
      const sub = catalogEntry.subComponents.find(s => s.name === cfg.name);

      if (sub?.type === "MULT") multIndices.push(i);
    });

    if (multIndices.length <= 1) return;

    const firstIp = watchedSKUData[multIndices[0]]?.ipAddress ?? "";

    multIndices.slice(1).forEach(i => {
      if (watchedSKUData[i]?.ipAddress !== firstIp) {
        setValue(`skuDataConfigurations.${i}.ipAddress`, firstIp);
      }
    });
  }, [watchedSKUData, watchedSKUNumber]);

  // When the SKU number changes, auto-populate skuDataConfigurations from the catalog
  const handleSKUNumberChange = (skuNumber: string) => {
    setValue("skuNumber", skuNumber, { shouldValidate: true });

    const catalogEntry = skuCatalog[skuNumber];

    if (!catalogEntry) return;

    // Auto-fill configuration name with product name (editable by user)
    setValue("configurationName", catalogEntry.productName, {
      shouldValidate: true,
    });

    // Build one model-assignment entry per subcomponent.
    // AGX/POE nodes get an empty models array (they're processing nodes).
    const agxNodes = catalogEntry.subComponents.filter(s => s.type === "AGX");

    // Build an ordered list of unique device types by first appearance,
    // then map each type to an AGX node sequentially (clamped to last node).
    const seenTypes = new Set<string>();
    const uniqueDeviceTypes: string[] = [];

    for (const sub of catalogEntry.subComponents) {
      if (
        sub.type !== "AGX" &&
        sub.type !== "POE" &&
        !seenTypes.has(sub.type)
      ) {
        seenTypes.add(sub.type);

        uniqueDeviceTypes.push(sub.type);
      }
    }

    const deviceTypeToNode: Record<string, string | undefined> = {};

    uniqueDeviceTypes.forEach((type, i) => {
      deviceTypeToNode[type] = agxNodes[Math.min(i, agxNodes.length - 1)]?.name;
    });

    const configurations = catalogEntry.subComponents.map(sub => {
      const isDevice = sub.type !== "AGX" && sub.type !== "POE";
      const assignedNode =
        catalogEntry.nodeCount > 1 && isDevice
          ? deviceTypeToNode[sub.type]
          : undefined;

      return {
        name: sub.name,
        models:
          (ALLOWED_MODELS_BY_TYPE[sub.type] ?? []).length === 1
            ? // Auto-select the only option for single-model types (CH4, VOC, LWOP)
              [...(ALLOWED_MODELS_BY_TYPE[sub.type] ?? [])]
            : [],
        ...(assignedNode && { defaultNodeName: assignedNode }),
      };
    });

    const sortedConfigurations = sortSKUConfigurations(
      configurations,
      catalogEntry.subComponents
    );

    setValue("skuDataConfigurations", sortedConfigurations);
  };

  const onSubmit = async (formData: ISKUForm) => {
    const catalogEntry = skuCatalog[formData.skuNumber];

    if (
      existingConfigurationNames &&
      existingConfigurationNames.includes(formData.configurationName)
    ) {
      errorNotification("Template name must be unique");

      setError("configurationName", {
        type: "manual",
        message: "Template name must be unique",
      });

      return;
    }

    if (
      formData.password &&
      formData.password.length > 0 &&
      formData.password.length < 8
    ) {
      const { errorNotification } = await import(
        "../../../../../common/variables/notification"
      );

      errorNotification("Password must be at least 8 characters");

      return;
    }

    const { password, hasRecordVideo, ...skuFormData } = formData;

    const sharedPayload = {
      ...skuFormData,
      // nodeCount comes from the catalog — not from user input
      nodeCount: catalogEntry?.nodeCount ?? 0,
      skuDataConfigurations: JSON.stringify(
        formData.skuDataConfigurations.map(device => ({
          ...device,
          hasRecordVideo,
          ...(password && { password }),
          ...(device.ipAddress?.trim() && {
            ipAddress: device.ipAddress.trim(),
          }),
        }))
      ),
    };

    if (mode === "edit") {
      if (!id) {
        errorNotification("Cannot update template: missing id");

        return;
      }

      await updateSKU({ id, ...sharedPayload });
    } else {
      await createSKU(sharedPayload);
    }

    closeDialog();
  };

  const closeDialog = (): void => {
    reset();

    handleClose();
  };

  const confirmDialogClose = (): void => {
    confirm({
      title:
        mode === "duplicate" || mode === "edit"
          ? "Are you sure? Your changes will not be saved."
          : "Are you sure you want to cancel?",
      confirmationText: "Cancel",
      cancellationText: "Back",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
      },
    })
      .then(async (): Promise<void> => {
        closeDialog();
      })
      .catch(() => {
        // User clicked "Back" — keep dialog open
      });
  };

  const selectedCatalogEntry = watchedSKUNumber
    ? skuCatalog[watchedSKUNumber]
    : null;

  return (
    <StyledDialog
      open={open}
      title={
        mode === "duplicate"
          ? "Customize and save as new system template"
          : mode === "edit"
          ? "Edit system template"
          : "Create system template"
      }
      onClose={confirmDialogClose}
      maxWidth="md"
      showSubmitButton={true}
      SubmitButtonProps={{
        loading,
        title:
          mode === "duplicate"
            ? "Save as new system template"
            : mode === "edit"
            ? "Save changes"
            : "Create system template",
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="flex-start">
        {/* SKU Number — drives the hardware component list */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="skuNumber"
            control={control}
            rules={{ required: "SKU Number is required" }}
            render={({ field }) => (
              <TextField
                select
                required
                fullWidth
                label="SKU Number"
                value={field.value ?? ""}
                onChange={e => handleSKUNumberChange(e.target.value)}
                disabled={mode === "duplicate" || mode === "edit"}
                error={errors.skuNumber !== undefined}
                helperText={
                  errors.skuNumber?.message ||
                  (mode === "duplicate"
                    ? "SKU number is fixed for duplicates"
                    : mode === "edit"
                    ? "SKU number cannot be changed"
                    : "Selects hardware component list from catalog")
                }
              >
                {Object.entries(skuCatalog)
                  .filter(([key]) => key !== "000-000")
                  .map(([key, entry]) => (
                    <MenuItem key={key} value={key}>
                      {key} · {entry.productName}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
        </Grid>

        {/* Configuration Name — editable label for this deployment template */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="configurationName"
            control={control}
            rules={{ required: "Template name is required" }}
            render={({ field }) => (
              <TextField
                fullWidth
                required
                label="Template Name"
                {...field}
                value={field.value ?? ""}
                error={errors.configurationName !== undefined}
                helperText={
                  errors.configurationName?.message ||
                  "Descriptive name for this deployment template"
                }
              />
            )}
          />
        </Grid>

        {/* Password */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{
              validate: value => {
                if (value && value.length > 0 && value.length < 8) {
                  return "Password must be at least 8 characters";
                }

                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                fullWidth
                label="RTSP user:password"
                {...field}
                placeholder="Device password"
                error={
                  !!errors?.password ||
                  !!(
                    field.value &&
                    field.value.length > 0 &&
                    field.value.length < 8
                  )
                }
                helperText={
                  errors?.password?.message ||
                  (field.value &&
                  field.value.length > 0 &&
                  field.value.length < 8
                    ? "Must be at least 8 characters"
                    : "This will be the default credential used when onboarding devices.")
                }
              />
            )}
          />
        </Grid>

        {/* Enable Recording */}
        <Grid item xs={12}>
          <Controller
            name="hasRecordVideo"
            control={control}
            render={({ field }) => (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">Enable Recording</Typography>
                <Switch
                  checked={field.value ?? true}
                  onChange={e => field.onChange(e.target.checked)}
                />
              </Stack>
            )}
          />
        </Grid>
      </Grid>

      {/* Component model assignments — populated from catalog when SKU is selected */}
      {selectedCatalogEntry && watchedSKUData.length > 0 && (
        <>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {selectedCatalogEntry.productName} —{" "}
            {selectedCatalogEntry.subComponents.length} components,{" "}
            {selectedCatalogEntry.nodeCount} AGX node
            {selectedCatalogEntry.nodeCount !== 1 ? "s" : ""}
          </Typography>
          <Box sx={{ mb: 2 }}>
            {(() => {
              const agxNodeNames = selectedCatalogEntry.subComponents
                .filter(s => s.type === "AGX")
                .map(s => s.name);

              // First MULT index in the sorted data — only it shows the IP input
              const firstMultDataIndex = watchedSKUData.findIndex(cfg => {
                const sub = selectedCatalogEntry.subComponents.find(
                  s => s.name === cfg.name
                );

                return sub?.type === "MULT";
              });

              return watchedSKUData.map((cfg, index) => {
                const sub = selectedCatalogEntry.subComponents.find(
                  s => s.name === cfg.name
                );

                if (!sub) return null;

                return (
                  <SKUFormItem
                    key={cfg.name + index}
                    index={index}
                    componentName={sub.name}
                    componentType={sub.type}
                    control={control}
                    errors={errors}
                    nodeCount={selectedCatalogEntry.nodeCount}
                    agxNodeNames={agxNodeNames}
                    hideIpField={
                      sub.type === "MULT" && index !== firstMultDataIndex
                    }
                  />
                );
              });
            })()}
          </Box>
        </>
      )}
    </StyledDialog>
  );
};

export default CreateSKUDialog;
